import React, { useState, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import WizardStepLayout from '../WizardStepLayout';
import { WIZARD_ACTIONS } from '../constants';
import { SHAPE_TYPE } from '../../utils/constants';

const MIN_CHARS = 3;
const DEBOUNCE_MS = 200;
const DROPDOWN_Z = 10050;

async function loadPlacesLibrary() {
  if (!window.google?.maps?.importLibrary) return null;
  try {
    return await window.google.maps.importLibrary('places');
  } catch (e) {
    console.warn('Could not load Google Places library', e);
    return null;
  }
}

function placePredictionDescription(pred) {
  if (!pred) return '';
  const t = pred.text;
  if (typeof t === 'string') return t;
  if (t?.text) return t.text;
  return '';
}

/** Leaflet bounds → optional google.maps.LatLngBounds for legacy predictions */
function leafletBoundsToGoogleBounds(mapBounds) {
  if (!mapBounds || !window.google?.maps?.LatLngBounds) return null;
  const sw = mapBounds.getSouthWest?.();
  const ne = mapBounds.getNorthEast?.();
  if (!sw || !ne || sw.lat == null || ne.lat == null) return null;
  return new window.google.maps.LatLngBounds(
    { lat: sw.lat, lng: sw.lng },
    { lat: ne.lat, lng: ne.lng },
  );
}

function snapToExistingLocation(lat, lng, address, locations) {
  if (!locations || locations.length === 0) return { lat, lng };

  const typedAddr = address?.trim().toLowerCase() || '';
  const exactMatch = locations.find(
    (loc) =>
      loc.address &&
      loc.loc_lat != null &&
      loc.loc_lng != null &&
      loc.address.trim().toLowerCase() === typedAddr,
  );
  if (exactMatch) return { lat: exactMatch.loc_lat, lng: exactMatch.loc_lng };

  const threshKm = 0.01524;
  let closestDist = Infinity;
  let snapped = { lat, lng };
  locations.forEach((loc) => {
    if (loc.loc_lat != null && loc.loc_lng != null) {
      const dLat = (loc.loc_lat - lat) * (Math.PI / 180);
      const dLng = (loc.loc_lng - lng) * (Math.PI / 180);
      const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(lat * (Math.PI / 180)) *
          Math.cos(loc.loc_lat * (Math.PI / 180)) *
          Math.sin(dLng / 2) ** 2;
      const d = 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      if (d < closestDist && d <= threshKm) {
        closestDist = d;
        snapped = { lat: loc.loc_lat, lng: loc.loc_lng };
      }
    }
  });
  return snapped;
}

const LocationStep = ({
  state,
  dispatch,
  onNext,
  onMapClick,
  isMapSelectActive,
  onSwitchToLegacy,
  locations,
  mapRef,
  setSelectMode,
  setSelectedAddressLatLng,
  setSelectedAddress,
  onActivatePolygon,
  onActivateMarker,
  onActivateLine,
  onRedrawPolygon,
  onRedrawLine,
  isDrawingPolygon,
  isDrawingLine,
  schemaReady,
}) => {
  const [inputValue, setInputValue] = useState(state.address || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const dropdownRef = useRef(null);
  const addressInputRef = useRef(null);
  const [dropdownFixedRect, setDropdownFixedRect] = useState(null);
  const sessionTokenRef = useRef(null);
  const requestIdRef = useRef(0);
  const debounceRef = useRef(null);

  const isPolygonMode = state.shapeType === SHAPE_TYPE.POLYGON;
  const isLineMode = state.shapeType === SHAPE_TYPE.LINE;
  const hasPolygon = Array.isArray(state.polygonCoordinates) && state.polygonCoordinates.length >= 3;
  const hasLine = Array.isArray(state.lineCoordinates) && state.lineCoordinates.length >= 2;
  const hasLocation = isPolygonMode ? hasPolygon : isLineMode ? hasLine : Boolean(state.latLng);

  useEffect(() => {
    if (state.address && state.address !== inputValue) {
      setInputValue(state.address);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.address]);

  const fetchSuggestions = useCallback(
    (value) => {
      const thisRequest = ++requestIdRef.current;

      const run = async () => {
        const lib = await loadPlacesLibrary();
        if (thisRequest !== requestIdRef.current) return;

        const leafletBounds = mapRef?.current?.getBounds?.();

        if (lib?.AutocompleteSuggestion?.fetchAutocompleteSuggestions) {
          const Token = lib.AutocompleteSessionToken;
          if (Token && !sessionTokenRef.current) {
            sessionTokenRef.current = new Token();
          }
          const request = { input: value, sessionToken: sessionTokenRef.current };
          if (leafletBounds) {
            const sw = leafletBounds.getSouthWest();
            const ne = leafletBounds.getNorthEast();
            const latPad = (ne.lat - sw.lat) * 0.5;
            const lngPad = (ne.lng - sw.lng) * 0.5;
            request.locationRestriction = {
              west: sw.lng - lngPad,
              north: ne.lat + latPad,
              east: ne.lng + lngPad,
              south: sw.lat - latPad,
            };
          }
          try {
            const response = await lib.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
            if (thisRequest !== requestIdRef.current) return;
            const list = (response.suggestions || [])
              .map((s) => {
                const pred = s.placePrediction;
                if (!pred) return null;
                const placeId = pred.placeId;
                const description = placePredictionDescription(pred);
                return placeId && description ? { place_id: placeId, description } : null;
              })
              .filter(Boolean);
            setSuggestions(list);
            setShowDropdown(list.length > 0);
            setHighlightIdx(-1);
          } catch {
            if (thisRequest === requestIdRef.current) {
              setSuggestions([]);
              setShowDropdown(false);
            }
          }
          return;
        }

        const places = window.google?.maps?.places;
        if (!places?.AutocompleteService) {
          if (thisRequest === requestIdRef.current) {
            setSuggestions([]);
            setShowDropdown(false);
          }
          return;
        }

        const service = new places.AutocompleteService();
        const req = { input: value };
        const gBounds = leafletBoundsToGoogleBounds(leafletBounds);
        if (gBounds) {
          req.bounds = gBounds;
          req.strictBounds = false;
        }
        service.getPlacePredictions(req, (predictions, status) => {
          if (thisRequest !== requestIdRef.current) return;
          if (status !== places.PlacesServiceStatus.OK || !predictions?.length) {
            setSuggestions([]);
            setShowDropdown(false);
            return;
          }
          setSuggestions(
            predictions.map((p) => ({
              place_id: p.place_id,
              description: p.description,
            })),
          );
          setShowDropdown(true);
          setHighlightIdx(-1);
        });
      };

      run();
    },
    [mapRef],
  );

  const handleInputChange = useCallback(
    (e) => {
      const val = e.target.value;
      setInputValue(val);
      if (state.latLng) {
        dispatch({ type: WIZARD_ACTIONS.SET_LATLNG, payload: null });
        dispatch({ type: WIZARD_ACTIONS.SET_ADDRESS, payload: '' });
        if (setSelectedAddressLatLng) setSelectedAddressLatLng(null);
      }
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (val.length >= MIN_CHARS) {
        debounceRef.current = setTimeout(() => fetchSuggestions(val), DEBOUNCE_MS);
      } else {
        setSuggestions([]);
        setShowDropdown(false);
      }
    },
    [fetchSuggestions, dispatch, state.latLng, setSelectedAddressLatLng],
  );

  useEffect(() => {
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, []);

  const selectSuggestion = useCallback(
    async (suggestion) => {
      setShowDropdown(false);
      setSuggestions([]);
      setInputValue(suggestion.description);
      sessionTokenRef.current = null;

      if (setSelectMode) setSelectMode(false);

      let formattedAddress = null;
      let lat = null;
      let lng = null;

      const lib = await loadPlacesLibrary();
      if (lib?.Place) {
        try {
          const place = new lib.Place({ id: suggestion.place_id });
          await place.fetchFields({ fields: ['formattedAddress', 'location'] });
          formattedAddress = place.formattedAddress || null;
          const loc = place.location;
          if (loc) {
            lat = typeof loc.lat === 'function' ? loc.lat() : loc.lat;
            lng = typeof loc.lng === 'function' ? loc.lng() : loc.lng;
          }
        } catch (err) {
          console.error('Place details error:', err);
        }
      }

      if ((lat == null || lng == null || !formattedAddress) && window.google?.maps?.places?.PlacesService) {
        const node = mapRef?.current?.getContainer?.() || document.createElement('div');
        await new Promise((resolve) => {
          const svc = new window.google.maps.places.PlacesService(node);
          svc.getDetails(
            {
              placeId: suggestion.place_id,
              fields: ['formatted_address', 'geometry', 'name'],
            },
            (place, status) => {
              if (
                status === window.google.maps.places.PlacesServiceStatus.OK &&
                place?.geometry?.location
              ) {
                formattedAddress = formattedAddress || place.formatted_address || place.name;
                lat = place.geometry.location.lat();
                lng = place.geometry.location.lng();
              }
              resolve();
            },
          );
        });
      }

      if (formattedAddress != null && lat != null && lng != null) {
        const snapped = snapToExistingLocation(lat, lng, formattedAddress, locations);
        dispatch({ type: WIZARD_ACTIONS.SET_ADDRESS, payload: formattedAddress });
        dispatch({ type: WIZARD_ACTIONS.SET_LATLNG, payload: snapped });
        setInputValue(formattedAddress);
        if (setSelectedAddressLatLng) setSelectedAddressLatLng(snapped);
        if (setSelectedAddress) setSelectedAddress(formattedAddress);
      }
    },
    [dispatch, locations, mapRef, setSelectMode, setSelectedAddressLatLng, setSelectedAddress],
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (!showDropdown || suggestions.length === 0) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightIdx((prev) => Math.min(prev + 1, suggestions.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightIdx((prev) => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (highlightIdx >= 0 && highlightIdx < suggestions.length) {
          selectSuggestion(suggestions[highlightIdx]);
        }
      } else if (e.key === 'Escape') {
        setShowDropdown(false);
      }
    },
    [showDropdown, suggestions, highlightIdx, selectSuggestion],
  );

  useLayoutEffect(() => {
    if (!showDropdown || suggestions.length === 0) {
      setDropdownFixedRect(null);
      return undefined;
    }
    const update = () => {
      const el = addressInputRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setDropdownFixedRect({
        top: r.bottom,
        left: r.left,
        width: r.width,
      });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [showDropdown, suggestions.length, inputValue]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const inWrapper = dropdownRef.current?.contains(e.target);
      const inPortal = e.target.closest?.('.wizard__autocomplete-dropdown--portal');
      if (!inWrapper && !inPortal) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <WizardStepLayout
      stepNumber={1}
      onNext={onNext}
      nextDisabled={!hasLocation || !schemaReady}
      showBack={false}
      topRightExtra={
        <button className="wizard__link" onClick={onSwitchToLegacy}>
          Add without wizard.
        </button>
      }
    >
      {isPolygonMode ? (
        /* Polygon mode content */
        <div className="wizard__polygon-content">
          {hasPolygon ? (
            <>
              <div className="wizard__polygon-done">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#5a7a3a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 6L9 17l-5-5"/>
                </svg>
                <div>
                  <p className="wizard__polygon-done-title">Region drawn</p>
                  <p className="wizard__polygon-done-detail">
                    {state.polygonCoordinates.length}-point region
                  </p>
                </div>
              </div>
              <button
                className="wizard__btn-outlined"
                onClick={onRedrawPolygon}
              >
                Redraw region
              </button>
            </>
          ) : (
            <>
              <p className="wizard__polygon-heading">Draw a Region</p>
              <p className="wizard__polygon-description">
                Use the polygon tool on the map to draw a loop and finish the region.
              </p>
              {isDrawingPolygon && (
                <p className="wizard__helper-text">
                  Click on the map to place points. Click the first point to close the region.
                </p>
              )}
            </>
          )}
        </div>
      ) : isLineMode ? (
        /* Line mode content */
        <div className="wizard__polygon-content">
          {hasLine ? (
            <>
              <div className="wizard__polygon-done">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#5a7a3a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 6L9 17l-5-5"/>
                </svg>
                <div>
                  <p className="wizard__polygon-done-title">Line drawn</p>
                  <p className="wizard__polygon-done-detail">
                    {state.lineCoordinates.length}-point line
                  </p>
                </div>
              </div>
              <button
                className="wizard__btn-outlined"
                onClick={onRedrawLine}
              >
                Redraw line
              </button>
            </>
          ) : (
            <>
              <p className="wizard__polygon-heading">Draw a Line</p>
              <p className="wizard__polygon-description">
                Use the line tool on the map to drop points along a path.
              </p>
              {isDrawingLine && (
                <p className="wizard__helper-text">
                  Click on the map to add points. Double-click to finish the line.
                </p>
              )}
            </>
          )}
        </div>
      ) : (
        /* Marker mode content */
        <>
          <div className="wizard__autocomplete" ref={dropdownRef}>
            <input
              ref={addressInputRef}
              className="wizard__input"
              type="text"
              placeholder="Start typing an address..."
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onFocus={() => { if (suggestions.length > 0) setShowDropdown(true); }}
            />
            {showDropdown &&
              suggestions.length > 0 &&
              dropdownFixedRect &&
              createPortal(
                <ul
                  className="wizard__autocomplete-dropdown wizard__autocomplete-dropdown--portal"
                  style={{
                    position: 'fixed',
                    top: dropdownFixedRect.top,
                    left: dropdownFixedRect.left,
                    width: dropdownFixedRect.width,
                    zIndex: DROPDOWN_Z,
                  }}
                >
                  {suggestions.map((s, idx) => (
                    <li
                      key={s.place_id}
                      className={
                        'wizard__autocomplete-item' +
                        (idx === highlightIdx ? ' wizard__autocomplete-item--highlight' : '')
                      }
                      onMouseDown={() => selectSuggestion(s)}
                      onMouseEnter={() => setHighlightIdx(idx)}
                    >
                      {s.description}
                    </li>
                  ))}
                </ul>,
                document.body,
              )}
          </div>

          <div className="wizard__or-divider">or</div>

          <button
            className={
              'wizard__btn-outlined' +
              (isMapSelectActive ? ' wizard__btn-outlined--active' : '')
            }
            onClick={onMapClick}
          >
            Click a spot on the map
          </button>

          <p className="wizard__helper-text">
            You can drag the pin to adjust the location after placing it.
          </p>
        </>
      )}
    </WizardStepLayout>
  );
};

export default React.memo(LocationStep);
